import { Controller } from "@hotwired/stimulus"

// Web dashboard add actions (budgets/index.html+web.erb). They open the app's
// own add-expense sheet (voice-modal + expense-deck) on the right panel:
//   Smart expense  → the sheet, opened on Scan; Type sits one tab over and
//                    is parsed by budgets#parse_expense
//   Scan a receipt → a photo picked from disk, sent down the same scan road
//                    the native picker uses (voice-modal#scanImage)
// There is no camera and no microphone on web.
const AI_CONSENT_STORAGE_KEY = "floosyo:ai-consent:v2"
const SCAN_INDEX = 0
// Same envelope the native picker hands over: a downsampled JPEG, well under
// BudgetsController::MAX_IMAGE_DATA_URL_BYTES.
const MAX_EDGE_PX = 1600
const MAX_DATA_URL_BYTES = 1_900_000
const QUALITIES = [0.82, 0.68, 0.5]

export default class extends Controller {
  static targets = ["file"]

  // Opens on Scan, the tab the deck rests on, so nothing slides on the way in.
  smart() {
    this.#openOn(SCAN_INDEX)
  }

  // With consent on record the file picker opens straight away. Without it the
  // sheet opens on Scan behind the consent screen, and its Choose photo button
  // (the same file input) takes over once consent is given.
  scan() {
    if (this.#hasConsent() && this.hasFileTarget) {
      this.fileTarget.click()
      return
    }
    this.#openOn(SCAN_INDEX)
  }

  async picked(event) {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ""
    if (!file) return

    const modal = this.#modal
    if (!modal) return
    if (!modal._sheetOpen) this.#openOn(SCAN_INDEX)

    try {
      const dataUrl = await this.#jpegDataUrl(file)
      requestAnimationFrame(() => requestAnimationFrame(() => modal.scanImage(dataUrl)))
    } catch (error) {
      console.error("[WebAdd] receipt could not be read:", error)
      modal.scanFailed()
    }
  }

  #openOn(index) {
    const modal = this.#modal
    if (!modal) return
    this.#deck?.selectIndex(index)
    modal.open()
  }

  async #jpegDataUrl(file) {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close?.()

    for (const quality of QUALITIES) {
      const dataUrl = canvas.toDataURL("image/jpeg", quality)
      if (dataUrl.length <= MAX_DATA_URL_BYTES) return dataUrl
    }
    throw new Error("receipt too large after compression")
  }

  #hasConsent() {
    try {
      return localStorage.getItem(AI_CONSENT_STORAGE_KEY) === "1"
    } catch {
      return false
    }
  }

  get #modal() {
    return this.application.getControllerForElementAndIdentifier(this.element, "voice-modal")
  }

  get #deck() {
    const sheet = this.#modal?.sheetTarget
    return sheet ? this.application.getControllerForElementAndIdentifier(sheet, "expense-deck") : null
  }
}
